/**
 * What a parent needs to do something about, gathered across their children.
 *
 * The parent dashboard is an inbox, not a report: My Family is where the record
 * lives. So this returns only things with an action attached, ordered by how
 * much they matter, and returns nothing at all on a quiet day — an empty
 * dashboard is the correct answer when a family is up to date.
 */
import { formatChecklistDate } from '~/pages/FamilyDetail/checklistWords';

export type ParentInboxKind =
  | 'MISSING_WORK'
  | 'INVOICE_OVERDUE'
  | 'ENROLLMENT_ACTION'
  | 'LOW_GRADE'
  | 'INVOICE_DUE';

export type Urgency = 'danger' | 'warning' | 'normal';

export interface ParentInboxItem {
  key: string;
  kind: ParentInboxKind;
  title: string;
  detail: string;
  urgency: Urgency;
  /** schoolYearId: the year to select before going to `to`. */
  action: { label: string; to: string; external?: boolean; schoolYearId?: string };
}

export interface MissingAssignment {
  assignmentId: string;
  name: string;
  className: string;
  dueOn?: string | null;
}

export interface ClassGrade {
  transcriptName: string;
  result?: { percentage?: number | null; letterGrade?: string | null; gradeColor?: string | null } | null;
}

export interface StudentSummary {
  personId: string;
  name: string;
  missingAssignments: MissingAssignment[];
  classGrades: ClassGrade[];
}

export interface InvoiceLike {
  id: string;
  /** The billing snapshot's human-readable status, e.g. "Unpaid", "Paid". */
  status: string;
  dueDate?: string | null;
  amountCents: number;
  /** What the invoice is for, e.g. "School Photo Fee — October 2026". */
  memo?: string | null;
}

/** An invoice from a year other than the selected one. */
export interface OtherYearInvoiceLike extends InvoiceLike {
  schoolYear: { id: string; name: string };
}

/**
 * The only invoice states where the family owes an action. Everything else —
 * paid, draft, scheduled, processing, void — is either finished or somebody
 * else's move, and putting it on the dashboard would be noise.
 */
const ACTIONABLE_INVOICE_STATUSES = ['Unpaid', 'Payment Failed'];

export interface EnrollmentLike {
  state: string;
  enrollmentKind?: string | null;
  inviteToken?: string | null;
  /** SET for a year opened by the checklist invite, which has no old-flow link to continue. */
  accessSource?: string | null;
}

/** A school year with something for the family to do on its checklist. */
export interface YearNeedingYouLike {
  schoolYear: { name: string; slug: string };
  /** ENROLLED once the year is enrolled: what's left is extra, not the way in. */
  access?: string | null;
  checklist: {
    progress: { done: number; total: number; waitingOn?: readonly string[] | null };
    /** Every item the family sees; the year's overview lists the open and in-progress ones as still to do. */
    items?: readonly { title: string; personName?: string | null; state: string }[] | null;
  };
}

/** The items the year's overview lists under "Still to do" for an enrolled year. */
const STILL_TO_DO_STATES = ['OPEN', 'IN_PROGRESS'];

function stillToDo(items: NonNullable<YearNeedingYouLike['checklist']['items']>): string[] {
  return items
    .filter((item) => STILL_TO_DO_STATES.includes(item.state))
    .map((item) => (item.personName ? `${item.title} — ${item.personName.split(' ')[0]}` : item.title));
}

/**
 * What the school has switched on in School Settings. A parent dashboard shows
 * only what its school chose to put there, so an absent setting is treated the
 * way the server defaults it — everything but low grades.
 */
export interface ParentAlertsConfig {
  invoiceOverdue: boolean;
  invoiceDue: boolean;
  enrollmentAction: boolean;
  missingWork: boolean;
  lowGrade: boolean;
  /** The floor letter's minimum percentage, read off the school's grade scale. */
  lowGradeFloorPercentage?: number | null;
}

export const DEFAULT_ALERTS: ParentAlertsConfig = {
  invoiceOverdue: true,
  invoiceDue: true,
  enrollmentAction: true,
  missingWork: true,
  lowGrade: false,
  lowGradeFloorPercentage: null,
};

export interface BuildInboxInput {
  students: StudentSummary[];
  invoices: InvoiceLike[];
  /** Unpaid invoices from any of the family's years; those already in `invoices` are not listed twice. */
  otherYearInvoices?: readonly OtherYearInvoiceLike[];
  enrollment?: EnrollmentLike | null;
  /** Every year whose checklist needs the family, whichever year is selected. */
  yearsNeedingYou?: readonly YearNeedingYouLike[];
  /** Where the family's own pages live, e.g. /app/immanuel */
  basePath: string;
  today: string;
  alerts?: ParentAlertsConfig;
}

/** Enrollment states where the ball is in the family's court. */
const OPEN_ENROLLMENT_STATES = ['INVITED', 'IN_PROGRESS'];

const ORDER: Record<ParentInboxKind, number> = {
  INVOICE_OVERDUE: 0,
  ENROLLMENT_ACTION: 1,
  MISSING_WORK: 2,
  LOW_GRADE: 3,
  INVOICE_DUE: 4,
};

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

function listNames(items: { name: string }[], limit = 3): string {
  const names = items.slice(0, limit).map((item) => item.name);
  const rest = items.length - names.length;
  return rest > 0 ? `${names.join(', ')} and ${rest} more` : names.join(', ');
}

/**
 * The grades worth showing a parent right now.
 *
 * A student's classGrades span every term in the year, so the same subject
 * appears once per quarter and the ones that have not started carry no
 * percentage. Showing those verbatim gives a parent four "Bible —" rows and no
 * information, so only graded standings survive, one per subject.
 */
export function currentGrades<T extends ClassGrade>(classGrades: readonly T[]): T[] {
  const bySubject = new Map<string, T>();
  for (const grade of classGrades) {
    if (typeof grade.result?.percentage !== 'number') continue;
    if (!bySubject.has(grade.transcriptName)) bySubject.set(grade.transcriptName, grade);
  }
  return [...bySubject.values()];
}

export function buildParentInbox({
  students,
  invoices,
  otherYearInvoices = [],
  enrollment,
  yearsNeedingYou = [],
  basePath,
  today,
  alerts = DEFAULT_ALERTS,
}: BuildInboxInput): ParentInboxItem[] {
  const items: ParentInboxItem[] = [];

  // Money first: it has a deadline someone else set.
  const listed = new Set(invoices.map((invoice) => invoice.id));
  const elsewhere = otherYearInvoices.filter((invoice) => !listed.has(invoice.id));
  const owed: (InvoiceLike & { schoolYear?: OtherYearInvoiceLike['schoolYear'] })[] = [...invoices, ...elsewhere];
  for (const invoice of owed) {
    if (!ACTIONABLE_INVOICE_STATUSES.includes(invoice.status)) continue;
    const failed = invoice.status === 'Payment Failed';
    const overdue = !!invoice.dueDate && invoice.dueDate < today;
    if (failed || overdue ? !alerts.invoiceOverdue : !alerts.invoiceDue) continue;
    // Not every invoice is tuition, so the item names the invoice it is about.
    const due = invoice.dueDate ? `Due ${formatChecklistDate(invoice.dueDate)}` : 'No due date set';
    items.push({
      key: `invoice-${invoice.id}`,
      kind: failed || overdue ? 'INVOICE_OVERDUE' : 'INVOICE_DUE',
      title: invoice.schoolYear
        ? `Unpaid invoice for ${invoice.schoolYear.name}${invoice.memo ? `: ${invoice.memo}` : ''}`
        : failed
        ? `Payment failed — ${money(invoice.amountCents)}`
        : overdue
          ? `Payment overdue — ${money(invoice.amountCents)}`
          : `Payment due — ${money(invoice.amountCents)}`,
      detail: failed
        ? 'The card on file was declined.'
        : invoice.schoolYear ? `${money(invoice.amountCents)} · ${due}`
        : invoice.memo ? `${invoice.memo} · ${due}` : due,
      urgency: failed || overdue ? 'danger' : 'normal',
      action: {
        label: failed || overdue ? 'Pay now' : 'View invoice',
        to: `${basePath}/invoices`,
        ...(invoice.schoolYear && { schoolYearId: invoice.schoolYear.id }),
      },
    });
  }

  if (alerts.enrollmentAction && enrollment && enrollment.accessSource !== 'SET' && OPEN_ENROLLMENT_STATES.includes(enrollment.state) && enrollment.inviteToken) {
    const path = enrollment.enrollmentKind === 'RE_ENROLLMENT' ? 'enroll' : 'apply';
    items.push({
      key: 'enrollment',
      kind: 'ENROLLMENT_ACTION',
      title: enrollment.state === 'INVITED' ? 'Start your enrollment' : 'Finish your enrollment',
      detail: enrollment.state === 'INVITED'
        ? 'The school has invited your family to enroll for this year.'
        : 'Your enrollment form is started but not submitted.',
      urgency: 'warning',
      action: { label: 'Continue', to: `/app/${path}/${enrollment.inviteToken}`, external: true },
    });
  }

  // A year enrolling on its checklist, or enrolled with something still open,
  // is the family's to move, whichever year the app has selected: the item
  // names the year and goes to its own page.
  if (alerts.enrollmentAction) {
    for (const { schoolYear, access, checklist } of yearsNeedingYou) {
      const { done, total, waitingOn } = checklist.progress;
      // An enrolled year counts what its overview lists as still to do.
      const listed = access === 'ENROLLED' && checklist.items ? stillToDo(checklist.items) : null;
      const left = listed ? listed.length : waitingOn?.length ?? total - done;
      items.push({
        key: `enroll-${schoolYear.slug}`,
        kind: 'ENROLLMENT_ACTION',
        title: access === 'ENROLLED'
          ? `To do for ${schoolYear.name} · ${left} left`
          : `Enroll for ${schoolYear.name} · ${done} of ${total} done`,
        detail: (listed ?? waitingOn ?? []).length > 0
          ? `Still to do: ${(listed ?? waitingOn ?? []).join(', ')}`
          : access === 'ENROLLED'
            ? `The school has something for you to do for ${schoolYear.name}.`
            : `Your enrollment for ${schoolYear.name} is open.`,
        urgency: 'warning',
        action: { label: 'Continue', to: `${basePath}/years/${schoolYear.slug}/enroll` },
      });
    }
  }

  for (const student of students) {
    // One item per student, not per assignment: a parent wants to know their
    // child is behind, then see what in.
    if (alerts.missingWork && student.missingAssignments.length > 0) {
      const count = student.missingAssignments.length;
      items.push({
        key: `missing-${student.personId}`,
        kind: 'MISSING_WORK',
        title: `${student.name} has ${count} missing ${plural(count, 'assignment', 'assignments')}`,
        detail: listNames(student.missingAssignments),
        urgency: count >= 3 ? 'danger' : 'warning',
        action: { label: 'See work', to: `${basePath}/students/${student.personId}` },
      });
    }

    // The floor comes off the school's own grade scale, so "low" means what the
    // school says it means. No floor letter on the scale means no alert at all.
    const floor = alerts.lowGrade ? alerts.lowGradeFloorPercentage : null;
    const low = typeof floor === 'number'
      ? currentGrades(student.classGrades).filter((grade) => (grade.result?.percentage as number) < floor)
      : [];
    if (low.length > 0) {
      items.push({
        key: `low-grade-${student.personId}`,
        kind: 'LOW_GRADE',
        title: `${student.name} is below ${floor}% in ${low.length} ${plural(low.length, 'class', 'classes')}`,
        detail: low
          .map((grade) => `${grade.transcriptName} ${grade.result?.letterGrade ?? ''}`.trim())
          .join(' · '),
        urgency: 'warning',
        action: { label: 'See grades', to: `${basePath}/students/${student.personId}` },
      });
    }
  }

  return items.sort((first, second) => ORDER[first.kind] - ORDER[second.kind]);
}
